import {
  Activity,
  HeartHandshake,
  LayoutGrid,
  LibraryBig,
  ShieldCheck,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

/**
 * ZAFROI ecosystem app registry.
 *
 * Each entry describes one app in the ZAFROI ecosystem so the AppSwitcher
 * can render it as an "ecosystem launcher" tile. Consumer apps may pass
 * their own list to AppSwitcher (for environment-specific URLs, dark-launch
 * flags, etc.); `defaultApps` below is the canonical baseline.
 */

// Loose by design (ADR-037): the workspace app registry is RUNTIME (served by
// /auth/me), so adding a new app must NOT require a ui-kit type change. Known ids:
// catalog · operations · engage · workforce · kosh · platform.
export type ZafroiAppId = string;

export type ZafroiAppStatus = 'live' | 'beta' | 'coming-soon';

export interface ZafroiApp {
  id: ZafroiAppId;
  /** Display name shown in the launcher and topbar. */
  name: string;
  /** One-line description shown in the launcher tile. */
  description: string;
  /** Lucide icon component (or any React component). */
  icon: LucideIcon;
  /** Destination URL — absolute (cross-app) or relative (same-app). */
  url: string;
  /** Accent class used as the tile's iconography highlight. */
  accentClassName: string;
  /** Lifecycle status. `coming-soon` tiles render disabled. */
  status: ZafroiAppStatus;
}

// App entry URLs are env-overridable (ADR-023 Option B): a SUBDOMAIN build leaves
// VITE_APP_URL_* unset → the canonical subdomain URLs below; a WORKSPACE build
// (served under app.zafroi.com) sets them to same-origin paths (/operations/,
// /engage/, …) so the AppSwitcher keeps the user inside the unified workspace
// instead of bouncing back out to a subdomain. `e()` reads the consumer build's env.
const e = (k: string, fallback: string): string =>
  ((import.meta as unknown as { env?: Record<string, string | undefined> }).env?.[k]) || fallback;

export const defaultApps: ZafroiApp[] = [
  {
    id: 'catalog',
    name: 'Catalog',
    description:
      'What you sell — products, plans, and offerings. The master catalog every stage references.',
    icon: LibraryBig,
    url: e('VITE_APP_URL_CATALOG', 'https://catalog.zafroi.com/products'),
    accentClassName: 'text-primary',
    status: 'live',
  },
  {
    id: 'operations',
    name: 'Operations',
    description:
      'Inventory, fulfilment, procurement, and aftercare — the operational backbone.',
    icon: LayoutGrid,
    // App entry (gated route), NOT the apex marketing root. Landing on a
    // RequireAuth route lets the cross-subdomain SSO cookie sign the user in
    // instead of dropping them on the public marketing page / login form.
    url: e('VITE_APP_URL_OPERATIONS', 'https://zafroi.com/dashboard'),
    accentClassName: 'text-primary',
    status: 'live',
  },
  {
    id: 'engage',
    name: 'Engage',
    description:
      'Customer journeys, campaigns, and lifecycle communication across channels.',
    icon: HeartHandshake,
    url: e('VITE_APP_URL_ENGAGE', 'https://engage.zafroi.com'),
    accentClassName: 'text-accent',
    status: 'live',
  },
  {
    id: 'workforce',
    name: 'Workforce',
    description:
      'The human execution layer — people, responsibilities, and execution across the ecosystem.',
    icon: Users,
    url: e('VITE_APP_URL_WORKFORCE', 'https://workforce.zafroi.com'),
    accentClassName: 'text-info',
    status: 'live',
  },
  {
    id: 'kosh',
    name: 'Kosh',
    description:
      'Commercial & financial operations — customers, invoices, receivables, and collections.',
    icon: Wallet,
    url: e('VITE_APP_URL_KOSH', 'https://kosh.zafroi.com'),
    accentClassName: 'text-accent',
    status: 'live',
  },
];

/**
 * The control-plane app. Deliberately NOT in `defaultApps` — it's a
 * PLATFORM_ADMIN surface, so execution-plane apps (operations/engage/workforce)
 * don't surface it in their launchers. The platform app appends this to its own
 * AppSwitcher list. Flip status to 'live' once platform.zafroi.com is serving.
 */
export const platformApp: ZafroiApp = {
  id: 'platform',
  name: 'Platform',
  description:
    'The ecosystem control plane — tenants, identity, modules, subscriptions, governance.',
  icon: ShieldCheck,
  // Control-plane build → platform.zafroi.com. Workspace build sets
  // VITE_APP_URL_PLATFORM='/admin/' so a TENANT_ADMIN's tile opens their
  // account/self-service persona same-origin (app.zafroi.com/admin/, ADR-024).
  url: e('VITE_APP_URL_PLATFORM', 'https://platform.zafroi.com/dashboard'),
  accentClassName: 'text-primary',
  status: 'live',
};

/** Marker icon for "all apps" trigger surfaces. */
export const AppsLauncherIcon = Activity;

export function findApp(
  apps: ZafroiApp[],
  id: ZafroiAppId,
): ZafroiApp | undefined {
  return apps.find((app) => app.id === id);
}

/**
 * iconKey → icon + accent. The ONLY build-time coupling left for the runtime
 * registry (ADR-037): adding a new APP needs no ui-kit change, but a genuinely new
 * GLYPH does — so reuse an existing key where possible. Unknown keys fall back.
 */
const WORKSPACE_ICON: Record<string, { icon: LucideIcon; accent: string }> = {
  catalog: { icon: LibraryBig, accent: 'text-primary' },
  operations: { icon: LayoutGrid, accent: 'text-primary' },
  engage: { icon: HeartHandshake, accent: 'text-accent' },
  workforce: { icon: Users, accent: 'text-info' },
  kosh: { icon: Wallet, accent: 'text-accent' },
  platform: { icon: ShieldCheck, accent: 'text-primary' },
};

/** The per-app shape `/auth/me` returns (ADR-037 runtime registry). */
export interface RawWorkspaceApp {
  id: string;
  name: string;
  description: string;
  iconKey: string;
  url: string;
  status: ZafroiAppStatus;
}

/**
 * Resolve the runtime registry (`/auth/me` → `workspace.apps`) into renderable
 * tiles for the AppSwitcher's `apps` prop. Already permission-filtered server-side,
 * so consumers pass this WITHOUT a `permissions` prop (no double-filtering).
 */
export function toWorkspaceApps(raw: RawWorkspaceApp[] | null | undefined): ZafroiApp[] {
  if (!raw) return [];
  return raw.map((a) => {
    const ic = WORKSPACE_ICON[a.iconKey] ?? { icon: Activity, accent: 'text-primary' };
    return {
      id: a.id,
      name: a.name,
      description: a.description,
      icon: ic.icon,
      url: a.url,
      accentClassName: ic.accent,
      status: a.status,
    };
  });
}

// UNIFIED-ACCESS S4 (ADR-028) — per-user product access. Which RBAC catalog
// modules belong to each product; a product tile shows in the AppSwitcher only
// when the user holds at least one permission whose module is in that set. Keeps
// product visibility emergent from permissions (an Ops-only user won't see the
// Workforce/Engage/Kosh tiles). Keep in sync with the runtime permission catalog.
const PRODUCT_MODULES: Partial<Record<ZafroiAppId, string[]>> = {
  catalog: ['products', 'offerings', 'plans'],
  operations: [
    'orders', 'inventory', 'inventory-categories', 'finished-goods', 'barcodes',
    'vendors', 'raw-materials', 'purchase-requests', 'purchase-orders', 'customers',
    'invoices', 'dispatch', 'dispatch-labels', 'tracking', 'returns', 'refunds',
    'reports', 'cancel-requests', 'return-requests', 'my-reviews', 'job-work',
  ],
  workforce: ['org-structure', 'employees', 'attendance', 'shifts-holidays', 'payroll'],
  engage: ['engage-inbox', 'engage-channels', 'engage-contacts'],
  kosh: ['kosh-commercial', 'kosh-billing', 'kosh-receivables', 'kosh-visibility'],
};

/**
 * Filter an app list to the products the user actually has permissions in
 * (per-user product access, ADR-028). A tile survives when the user holds ≥1
 * permission whose module is in that product's set. Tiles with no product-module
 * mapping (e.g. Platform) are tier-gated only and always pass.
 *
 * Fail-open: when `permissions` is absent/empty (older cached session, dev
 * API-key) or the viewer is PLATFORM_ADMIN, the list is returned unfiltered.
 */
export function filterAppsByPermissions(
  apps: ZafroiApp[],
  permissions: string[] | null | undefined,
  tier?: string,
): ZafroiApp[] {
  if (tier === 'PLATFORM_ADMIN') return apps;
  if (!permissions || permissions.length === 0) return apps;
  const userModules = new Set(
    permissions.map((k) => k.slice(0, k.lastIndexOf('.'))).filter(Boolean),
  );
  return apps.filter((app) => {
    const mods = PRODUCT_MODULES[app.id];
    if (!mods) return true; // platform / unmapped → tier-gated only
    return mods.some((m) => userModules.has(m));
  });
}
